import { StencilProvider } from '@amzn/stencil-react-components/context';
import React, { useEffect, useState } from 'react';
import {
  getClientId,
  getUserIdentifier,
  ILogsContext,
  INLINE_SERVICE_NAME,
  LiveHelpChat,
  LiveHelpFAB,
  MetricEvents,
  myHRLoggerErrorLog,
  myHRLoggerInfoLog,
  PageActionsForOmnitureMetrics,
  PageName,
  recordErrorCountMetric,
} from '@amzn/myhr-livehelp-ui';
import { LaasClientId, OmnitureHelper, WebAnalytics } from '@amzn/myhr-ui-commons';
import { useBreakpoints } from '@amzn/stencil-react-components/responsive';
import defaultTheme from '@amzn/stencil-react-theme-default';
import { recordMetricForFab } from '../utils/moduleWebsiteLoggerUtils';
import { IIntentDetails } from '../../interfaces/common';
import ErrorBoundary from '../errors/ErrorBoundary';
import LanguagesAvailable from '../enums/localisation';
import { LiveHelpDOM } from './LiveHelpDOM';
import { FAB_RENDERING_OPERATION_NAME } from '../consts/constants';
import { STENCIL_FONT_SIZE } from '../consts/styles';

export interface ILiveHelpWidgetProps {
  clientId: LaasClientId;
  pageName: string;
  intentDetails?: IIntentDetails;
  preferredLanguage?: LanguagesAvailable;
  showOnDesktop?: boolean;
  showOnMobile?: boolean;
  draggable?: boolean;
}

const MyHRLiveHelpWidget = ({
  clientId,
  pageName,
  intentDetails,
  preferredLanguage,
  showOnDesktop = true,
  showOnMobile = true,
  draggable = false,
}: ILiveHelpWidgetProps) => {
  const { matches } = useBreakpoints();
  const [arePropsValid, setArePropsValid] = useState<boolean>(false);
  const showFAB = (showOnMobile && matches.s) || (showOnDesktop && !matches.s);

  // Validate clientId and pageName passed as props to component. If clientId or pageName is invalid, Error is thrown.
  useEffect(() => {
    if (!clientId || !pageName || pageName === '' || !Object.values(LaasClientId).includes(clientId as LaasClientId)) {
      // Used static pageName, otherwise multiple metrics would be made. Also didn't add clientId. These can be tracked in Logs
      const logsContext: ILogsContext = {
        serviceName: INLINE_SERVICE_NAME,
        pageName: PageName.LIVEHELP_INLINE_PAGE_NAME,
        operationName: 'PropsValidation',
        userIdentifier: getUserIdentifier(),
      };

      recordErrorCountMetric(
        logsContext.pageName,
        logsContext.operationName,
        'Required props pageName or clientId is invalid',
      );
      myHRLoggerErrorLog(
        `Invalid props passed while calling Livehelp Widget, pageName: "${pageName}" and clientId: "${clientId}"`,
        logsContext,
      );

      throw Error(`Invalid ClientId or PageName. ClientId : ${clientId}, PageName : ${pageName}`);
    }
    setArePropsValid(true);
  }, [pageName, clientId]);

  // Publish metrics on FAB render
  useEffect(() => {
    if (showFAB && arePropsValid) {
      WebAnalytics.sendOmnitureMetric({
        pageName: PageName.INLINE_OMNITURE_PAGE_NAME,
        pageAction: PageActionsForOmnitureMetrics.FAB_RENDER,
        hitType: OmnitureHelper.HitTypes.PageHit,
      });

      myHRLoggerInfoLog(`[${getUserIdentifier()}]: FAB rendered for user.`, {
        serviceName: INLINE_SERVICE_NAME,
        pageName,
        clientId: getClientId(clientId),
        operationName: FAB_RENDERING_OPERATION_NAME,
        userIdentifier: getUserIdentifier(),
      });

      // Record Request Count metric for Fab Render
      recordMetricForFab(
        FAB_RENDERING_OPERATION_NAME,
        INLINE_SERVICE_NAME,
        getClientId(clientId),
        pageName,
        MetricEvents.COUNT,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFAB, arePropsValid]);

  return (
    <StencilProvider
      theme={{
        ...defaultTheme,
        font: {
          ...defaultTheme.font,
          monospace: { ...defaultTheme.font.monospace, size: STENCIL_FONT_SIZE },
          primary: { ...defaultTheme.font.primary, size: STENCIL_FONT_SIZE },
        },
      }}
    >
      <ErrorBoundary clientId={clientId} pageName={pageName}>
        <LiveHelpDOM clientId={clientId} preferredLanguage={preferredLanguage} pageName={pageName}>
          {showFAB && arePropsValid && (
            <LiveHelpChat>
              <LiveHelpFAB
                intent={intentDetails}
                clientId={getClientId(clientId)}
                pageName={pageName}
                draggable={draggable}
              />
            </LiveHelpChat>
          )}
        </LiveHelpDOM>
      </ErrorBoundary>
    </StencilProvider>
  );
};

export default MyHRLiveHelpWidget;
